import { Column, Row, HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import React, { type JSX } from "react";
import styled from "styled-components";

import { AccordionText } from "./__internal__/AccordionText";
import { ChevronIcon } from "./__internal__/ChevronIcon";
import { CustomizedCollapsableComponent } from "./__internal__/CustomizedCollapsableComponent";
import useToggleAccordion from "./__internal__/useToggleAccordion";
import {
  getLineHeight,
  SCROLL_HEIGHT_MARGIN_OF_ERROR,
} from "./__internal__/utils";

interface IProps {
  title: string;
  text?: string;
  maxNumberOfLinesShownWhenCollapsed: number;
  visibleComponent?: JSX.Element;
  visibleComponentInSpan?: JSX.Element;
  onExpandAccordionItem?: () => void;
  isListOfItems?: boolean;
  collapsedComponent?: JSX.Element;
  expandedComponent?: JSX.Element;
  isCustomizedCollapsableComponent?: boolean;
  onView?: () => void;
}

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate/Accordion/Accordion#Accordion"
 */
export const AccordionContainer: React.FunctionComponent<IProps> = ({
  title,
  maxNumberOfLinesShownWhenCollapsed,
  text,
  visibleComponent,
  visibleComponentInSpan,
  onExpandAccordionItem,
  isListOfItems,
  collapsedComponent,
  expandedComponent,
  isCustomizedCollapsableComponent,
  onView,
}) => {
  const viewRef = useDoActionWhenVisible(onView);
  const [hasExpandableContent, setHasExpandableContent] = React.useState(true);
  const { isAccordionOpen, toggleIsAccordionOpen } = useToggleAccordion({
    hasExpandableContent,
    onExpandAccordionItem,
  });
  const [isFullTextVisible, setIsFullTextVisible] =
    React.useState(isAccordionOpen);
  const accordionContentRef = React.useRef<HTMLDivElement>(null);

  const maxHeightOfTextWhenCollapsed =
    maxNumberOfLinesShownWhenCollapsed *
    getLineHeight() *
    SCROLL_HEIGHT_MARGIN_OF_ERROR;
  const [textExpandedHeight, setTextExpandedHeight] = React.useState(
    maxHeightOfTextWhenCollapsed
  );

  React.useLayoutEffect(() => {
    if (!accordionContentRef.current) {
      return;
    }

    // The content height may change (for example when a product with a different description is selected)
    setTextExpandedHeight(accordionContentRef.current.scrollHeight);

    if (
      accordionContentRef.current.scrollHeight > maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(true);
    } else if (
      accordionContentRef.current.scrollHeight <= maxHeightOfTextWhenCollapsed
    ) {
      setHasExpandableContent(false);
    }
  }, [maxHeightOfTextWhenCollapsed, accordionContentRef.current?.scrollHeight]);

  React.useEffect(() => {
    if (isAccordionOpen) {
      setIsFullTextVisible(true);
    }
  }, [isAccordionOpen]);

  const handleTransitionEnd = React.useCallback(() => {
    if (!isAccordionOpen) {
      setIsFullTextVisible(false);
    }
  }, [isAccordionOpen]);

  return (
    <>
      <StyledAccordionContainer
        $hasExpandableContent={hasExpandableContent}
        onClick={toggleIsAccordionOpen}
        ref={onView ? viewRef : undefined}
      >
        <Column>
          <Typography variant="paragraphSansMedium">{title}</Typography>
          <Spacer size="1x" />
          {isCustomizedCollapsableComponent ? (
            <CustomizedCollapsableComponent
              $isAccordionOpen={isAccordionOpen}
              $componentExpandedHeight={expandedComponent?.props?.height}
              $componentCollapsedHeight={collapsedComponent?.props?.height}
            >
              {isAccordionOpen ? expandedComponent : collapsedComponent}
            </CustomizedCollapsableComponent>
          ) : (
            <AccordionText
              data-test-id="accordion-text"
              $isAccordionOpen={isAccordionOpen}
              $isFullTextVisible={isFullTextVisible}
              onTransitionEnd={handleTransitionEnd}
              ref={accordionContentRef}
              $maxNumberOfLinesShownWhenCollapsed={
                maxNumberOfLinesShownWhenCollapsed
              }
              $textExpandedHeight={textExpandedHeight}
              $textCollapsedHeight={maxHeightOfTextWhenCollapsed}
              $isListOfItems={isListOfItems}
            >
              <Typography variant="paragraphSansRegular">{text}</Typography>
              {visibleComponentInSpan}
            </AccordionText>
          )}

          {visibleComponent}
        </Column>
        {hasExpandableContent ? (
          <>
            <Spacer width="3x" />
            <ChevronIcon isAccordionOpen={isAccordionOpen} />
          </>
        ) : null}
      </StyledAccordionContainer>
      <HorizontalLine />
    </>
  );
};

const StyledAccordionContainer = styled(Row)<{
  $hasExpandableContent: boolean;
}>`
  width: 100%;
  padding: ${getSpacing("3x", 0)};
  cursor: ${({ $hasExpandableContent }) =>
    $hasExpandableContent ? "pointer" : "unset"};

  ${responsiveMediaQueries.mobile} {
    padding-top: ${getSpacing("1x", 0)};
  }
`;
